import {
  type CurrentUser,
  type LoginInput,
  type LoginResponse,
  type Permission,
} from '@edms/contracts';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api, ApiError, setAccessToken } from '@/lib/api-client';

interface AuthState {
  user: CurrentUser | null;
  status: 'checking' | 'authenticated' | 'anonymous';
}

interface AuthContextValue extends AuthState {
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  canAccessBranch: (branchId: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session applicative.
 *
 * Au démarrage, on tente un renouvellement silencieux : le cookie de
 * rafraîchissement suffit à rétablir la session après un rechargement de page,
 * sans jamais avoir persisté le jeton d'accès côté navigateur.
 */
export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<AuthState>({ user: null, status: 'checking' });

  const loadUser = useCallback(async (): Promise<void> => {
    try {
      const user = await api.get<CurrentUser>('/auth/me');
      setState({ user, status: 'authenticated' });
    } catch {
      setState({ user: null, status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const renewed = await api.refreshSession();
      if (cancelled) return;

      if (!renewed) {
        setState({ user: null, status: 'anonymous' });
        return;
      }

      await loadUser();
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  const login = useCallback(async (input: LoginInput): Promise<void> => {
    const response = await api.post<LoginResponse>('/auth/login', input);
    setAccessToken(response.accessToken);
    setState({ user: response.user, status: 'authenticated' });
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Une déconnexion ne doit jamais échouer du point de vue de
      // l'utilisateur : l'état local est nettoyé quoi qu'il arrive.
    }
    setAccessToken(null);
    setState({ user: null, status: 'anonymous' });
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set(state.user?.permissions ?? []);

    return {
      ...state,
      login,
      logout,
      refreshUser: loadUser,
      can: (permission) => permissions.has(permission),
      canAny: (...candidates) => candidates.some((permission) => permissions.has(permission)),
      canAccessBranch: (branchId) => {
        const accessible = state.user?.accessibleBranchIds;
        return accessible === null || accessible === undefined || accessible.includes(branchId);
      },
    };
  }, [state, login, logout, loadUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l’intérieur de <AuthProvider>.');
  }
  return context;
}

/** Traduit une erreur d'API en message affichable dans un formulaire. */
export function describeAuthError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Connexion impossible. Vérifiez votre réseau et réessayez.';
}
